import type { Bulletin } from '@shared/types/Bulletin';
import type { Ecriture } from '@shared/types/Ecriture';
import type { Salarie } from '@shared/types/Salarie';
import { PLAN_COMPTABLE_OHADA, lookupCompteOhada } from '@shared/constants/PlanComptable';
import { uuid } from '@shared/utils/uuid';
import { fmtMonth } from '@shared/utils/fmtDate';
import { roundFCFA } from '@shared/utils/round';
import { COMPTES_PAIE_PAR_DEFAUT, type TableComptesPaie } from './ComptesPaie';
import { PieceDesequilibreeError, totauxPiece } from './EquilibrePiece';

/**
 * Génération des écritures comptables à partir d’un bulletin de paie.
 *
 * SCHÉMA UNIQUE des écritures de paie. Les numéros de compte ne sont plus écrits ici : ils viennent de
 * la table de correspondance `ComptesPaie` (valeurs par défaut documentées, surchargeables par entreprise).
 * Valeurs par défaut :
 * - Débit 661x = cat1 + cat2 (brut imposable)
 * - Débit 648x = cat3 (transport, blanchissage, véhicule NC)
 * - Avantage en nature : charge 661 + contrepartie 791 (non monétaire, équilibre)
 * - Crédit dettes (4313/4314/4421/447x) + 422000 net à payer (SYSC-02) ; 421000 avances
 *
 * Équilibre strict : chaque bulletin produit une pièce dont Σdébit = Σcrédit à l'unité ;
 * sinon la génération est refusée (`PieceDesequilibreeError`), jamais une écriture bancale en base.
 */

export interface OptionsEcrituresPaie {
  genere_par?: string;
  /** Table de correspondance de l'entreprise (défaut : valeurs documentées). */
  comptes?: TableComptesPaie;
  /**
   * Période comptable d'imputation (AAAA-MM) si différente de celle du bulletin : correction d'une
   * période clôturée imputée sur une période ouverte (la période du bulletin reste dans le libellé).
   */
  periode_comptable?: string;
}

export class EcrituresPaieGenerator {
  static generer(
    bulletins: readonly Bulletin[],
    salaries: readonly Salarie[],
    options: OptionsEcrituresPaie = {},
  ): Ecriture[] {
    const salMap = new Map(salaries.map((s) => [s.id, s]));
    const C = options.comptes ?? COMPTES_PAIE_PAR_DEFAUT;
    const ecritures: Ecriture[] = [];

    for (const b of bulletins) {
      const salarie = salMap.get(b.salarie_id);
      const axe_cdc = salarie?.centre_cout;
      const lib = `Paie ${fmtMonth(b.periode)} - ${b.nom_complet ?? ''}`;
      const periodeEcr = options.periode_comptable ?? b.periode;
      const dateEcr = endOfMonthDate(periodeEcr);
      const tenant_id = b.tenant_id;
      const piece: Ecriture[] = [];

      const ajouterEcr = (compte: string, debit: number, credit: number, withAxe = false): void => {
        if (debit === 0 && credit === 0) return;
        const ecriture: Ecriture = {
          id: uuid('ecr'),
          tenant_id,
          date: dateEcr,
          periode: periodeEcr,
          journal: 'PA',
          compte,
          libelle_compte:
            PLAN_COMPTABLE_OHADA[compte]?.libelle ?? lookupCompteOhada(compte)?.libelle ?? '',
          libelle: lib,
          debit: roundFCFA(debit),
          credit: roundFCFA(credit),
          source: 'paie',
          salarie: b.nom_complet ?? null,
          salarie_id: b.salarie_id,
          matricule: b.matricule,
          genere_le: new Date().toISOString(),
        };
        if (options.genere_par) ecriture.genere_par = options.genere_par;
        if (withAxe && axe_cdc) ecriture.axe_cdc = axe_cdc;
        if (periodeEcr !== b.periode) ecriture.periode_origine = b.periode;
        piece.push(ecriture);
      };

      ajouterEcr(C.remuneration_base, b.brut_base, 0, true);
      if (b.sursalaire) ajouterEcr(C.remuneration_sursalaire, b.sursalaire, 0, true);

      /** Cat1/cat2 hors base/sursalaire/HS — restent en indemnités (661300 par défaut). */
      const transportCnss = b.transport_cnss ?? 0;
      const transportExonere =
        b.transport_exonere ?? (b.transport_cnss == null ? (b.transport ?? 0) : 0);

      const indemnitesImposables =
        (b.logement ?? 0) +
        (b.fonction_ind ?? 0) +
        (b.ind_vehicule_cnss ?? 0) +
        (b.ind_representation ?? 0) +
        (b.autres ?? 0) +
        (b.avantage_nature ?? 0) +
        (b.prime_anciennete ?? 0) +
        (b.prime ?? 0) +
        (b.prime_extra ?? 0) +
        (b.prime_logement ?? 0) +
        (b.prime_vehicule ?? 0) +
        (b.treizieme_mois ?? 0) +
        transportCnss;
      if (indemnitesImposables) ajouterEcr(C.remuneration_indemnites, indemnitesImposables, 0, true);
      if (b.hsup_total) ajouterEcr(C.remuneration_heures_sup, b.hsup_total, 0, true);

      /** Cat3 non cotisable → 648 (hors 661). Excédent transport = 661, pas 648. */
      const cat3 =
        transportExonere + (b.ind_blanchissage ?? 0) + (b.ind_vehicule_nc ?? 0);
      if (cat3) ajouterEcr(C.indemnites_non_cotisables, cat3, 0, true);

      /**
       * AEN inclus dans le brut (charge) mais retiré du net cash :
       * contrepartie 791 pour équilibrer le journal PA.
       */
      if (b.avantage_nature) ajouterEcr(C.avantage_nature_contrepartie, 0, b.avantage_nature);

      const cnssPatr = b.cnss_pf_emp + b.cnss_rp_emp + b.cnss_vid_emp;
      if (cnssPatr) ajouterEcr(C.charge_cnss_patronale, cnssPatr, 0, true);

      const cnamgsPatr = b.cnamgs_emp + b.cnamgs_fam_emp;
      if (cnamgsPatr) ajouterEcr(C.charge_cnamgs_patronale, cnamgsPatr, 0, true);

      const fgtls = b.fgtls_emp ?? 0;
      const taxesPatr = b.cfp_emp + b.fnh_emp + fgtls;
      if (taxesPatr) ajouterEcr(C.charge_taxes_patronales, taxesPatr, 0, true);

      if (cnssPatr || b.cnss_vid_sal) ajouterEcr(C.dette_cnss, 0, cnssPatr + b.cnss_vid_sal);
      if (cnamgsPatr || b.cnamgs_sal) ajouterEcr(C.dette_cnamgs, 0, cnamgsPatr + b.cnamgs_sal);
      if (b.irpp_mensuel) ajouterEcr(C.dette_irpp, 0, b.irpp_mensuel);
      if (b.cfp_emp) ajouterEcr(C.dette_cfp, 0, b.cfp_emp);
      if (b.fnh_emp) ajouterEcr(C.dette_fnh, 0, b.fnh_emp);
      /** TCS = retenue salariale (pas charge patronale). */
      if (b.tcs) ajouterEcr(C.dette_tcs, 0, b.tcs);
      /** CSS = retenue salariale (déduite du net cash). */
      if (b.css) ajouterEcr(C.dette_css, 0, b.css);
      /** FGTLS = charge patronale (Loi 3/94). */
      if (fgtls) ajouterEcr(C.dette_fgtls, 0, fgtls);

      /** SYSC-02 : net → rémunérations dues (422000) ; avance → avances et acomptes (421000). */
      ajouterEcr(C.dette_net, 0, b.net_a_payer);
      if (b.avance) ajouterEcr(C.dette_avances, 0, b.avance);

      assertPaieEquilibree(piece, b);
      ecritures.push(...piece);
    }

    return ecritures;
  }
}

/** Σdébit = Σcrédit à l'unité, sinon refus explicite (jamais d'écriture de paie déséquilibrée). */
function assertPaieEquilibree(piece: readonly Ecriture[], b: Bulletin): void {
  const { debit, credit } = totauxPiece(piece);
  if (debit !== credit) {
    const err = new PieceDesequilibreeError(debit, credit);
    err.message = `${err.message} (paie ${b.periode} — ${b.nom_complet ?? b.salarie_id})`;
    throw err;
  }
}

function endOfMonthDate(periode: string): string {
  const [y, m] = periode.split('-').map(Number);
  const last = new Date(y!, m!, 0).getDate();
  return `${String(last).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
}

/** Bulletins d’une période encore sans écriture de source paie. */
export function bulletinsSansEcriturePaie<
  TBulletin extends { salarie_id: string; periode: string },
  TEcriture extends {
    salarie_id?: string | null;
    periode: string;
    periode_origine?: string;
    source?: string;
  },
>(bulletins: TBulletin[], ecritures: TEcriture[], periode: string): TBulletin[] {
  const deja = new Set(
    ecritures
      .filter(
        (e) =>
          (e.periode_origine ?? e.periode) === periode &&
          (e.source === 'paie' || e.source === 'paie_correction'),
      )
      .map((e) => e.salarie_id)
      .filter((id): id is string => Boolean(id)),
  );
  return bulletins.filter((b) => b.periode === periode && !deja.has(b.salarie_id));
}

/** Helper fonction compat use cases : délègue à EcrituresPaieGenerator. */
export function generateEcrituresPaie(
  bulletin: import('@shared/types/Bulletin').Bulletin,
  salarie: import('@shared/types/Salarie').Salarie,
  comptes?: TableComptesPaie,
  /** Période comptable d'imputation (première période ouverte si celle du bulletin est clôturée). */
  periode_comptable?: string,
): import('@shared/types/Ecriture').Ecriture[] {
  return EcrituresPaieGenerator.generer([bulletin], [salarie], {
    ...(comptes ? { comptes } : {}),
    ...(periode_comptable ? { periode_comptable } : {}),
  });
}
